import {
    getDifficultyRating,
    getDurationRating,
    normalizePuzzleRatings,
    selectRatingMetrics,
    sortPuzzlesByDifficulty,
} from './kronologic-rating.mjs';
import {
    markerRotation,
    markerStateForEdge,
} from './kronologic-map.mjs';

const SELECTORS = {
    root: '[data-zgg-kronologic]',
    config: '.zgg-kronologic__config',
};

const SESSION_COOKIE = 'zgg_kronologic_session';
// Muss zur serverseitigen SESSION_TTL (12 Stunden) passen.
const SESSION_COOKIE_TTL_SECONDS = 12 * 60 * 60;

const readSessionCookie = () => {
    const entry = document.cookie
        .split('; ')
        .find((item) => item.startsWith(`${SESSION_COOKIE}=`));
    if (!entry) {
        return null;
    }

    try {
        const value = JSON.parse(decodeURIComponent(entry.slice(SESSION_COOKIE.length + 1)));
        return value && typeof value === 'object' ? value : null;
    } catch (error) {
        return null;
    }
};

const writeSessionCookie = (state) => {
    const value = encodeURIComponent(JSON.stringify(state));
    document.cookie = `${SESSION_COOKIE}=${value}; path=/; max-age=${SESSION_COOKIE_TTL_SECONDS}; SameSite=Lax`;
};

const clearSessionCookie = () => {
    document.cookie = `${SESSION_COOKIE}=; path=/; max-age=0; SameSite=Lax`;
};

// Direktlink, z. B. ?kronologic=Cuzco_Rike&spieler=2&start=1&loesung=aus. Das Puzzle wählt
// der Server vor (render_game_callback), Spielerzahl, Sofortstart und Lösungssperre
// übernimmt das Frontend; die Sperre gilt per Session auch serverseitig.
const readDirectLink = () => {
    const params = new URLSearchParams(window.location.search);
    const players = Number(params.get('spieler'));
    return {
        puzzleId: params.get('kronologic') || '',
        players: Number.isInteger(players) ? players : null,
        autoStart: ['1', 'ja', 'true'].includes((params.get('start') || '').toLowerCase()),
        solutionLocked: ['0', 'aus', 'nein', 'false'].includes((params.get('loesung') || '').toLowerCase()),
    };
};

const createElement = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) {
        element.className = className;
    }
    if (text !== undefined) {
        element.textContent = text;
    }
    return element;
};

class KronologicGame {
    constructor(root) {
        this.root = root;
        this.theme = (root.className.match(/zgg-kronologic--theme-([a-z0-9-]+)/) || [])[1] || 'paris-1920';
        this.config = this.readConfig();
        this.session = null;
        this.currentPlayer = null;
        this.selectedToken = null;
        this.stagedPlacement = null;
        this.pending = false;
        this.lastTurn = null;
        this.dropTarget = null;
        this.resizeObserver = null;
        this.selectWidgets = new Map();
        this.queriesUsed = new Set();
        this.repeatQueriesAllowed = true;
        this.directLink = readDirectLink();

        if (!this.config || this.config.errors?.length) {
            return;
        }

        this.puzzles = Array.isArray(this.config.puzzles) && this.config.puzzles.length
            ? this.config.puzzles
            : [{
                puzzleId: this.config.puzzleId,
                title: this.config.puzzleId,
                game: this.config.game,
                difficulty: this.config.difficulty,
                ratingMetrics: this.config.ratingMetrics,
                queries: this.config.queries,
                map: this.config.map,
                edges: this.config.edges,
                blockedEdges: this.config.blockedEdges,
                oneWayEdges: this.config.oneWayEdges,
                knownRoomTimeLimits: this.config.knownRoomTimeLimits,
                knownPositions: this.config.knownPositions,
                knownCurse: this.config.knownCurse,
            }];
        this.activePuzzle = this.puzzles.find((puzzle) => puzzle.puzzleId === this.config.puzzleId)
            || this.puzzles[0];
        this.applyPuzzleConfig(this.activePuzzle);

        this.elements = {
            title: root.querySelector('[data-kronologic-title]'),
            setup: root.querySelector('[data-kronologic-setup]'),
            game: root.querySelector('[data-kronologic-game]'),
            puzzleSelect: root.querySelector('[data-kronologic-puzzle-select]'),
            playerCount: root.querySelector('[data-kronologic-player-count]'),
            repeatQueries: root.querySelector('[data-kronologic-repeat-queries]'),
            difficulty: root.querySelector('[data-kronologic-difficulty]'),
            start: root.querySelector('[data-kronologic-start]'),
            status: root.querySelector('[data-kronologic-turn-status]'),
            edgeMarkers: root.querySelector('[data-kronologic-edge-markers]'),
            mapLegend: root.querySelector('[data-kronologic-map-legend]'),
            rooms: root.querySelector('[data-kronologic-rooms]'),
            timeTokens: root.querySelector('[data-kronologic-time-tokens]'),
            personTokens: root.querySelector('[data-kronologic-person-tokens]'),
            history: root.querySelector('[data-kronologic-history]'),
            historyEmpty: root.querySelector('[data-kronologic-history-empty]'),
            veil: root.querySelector('[data-kronologic-veil]'),
            veilTitle: root.querySelector('[data-kronologic-veil-title]'),
            ready: root.querySelector('[data-kronologic-ready]'),
            hintModal: root.querySelector('[data-kronologic-hint-modal]'),
            hintQuery: root.querySelector('[data-kronologic-hint-query]'),
            hintResult: root.querySelector('[data-kronologic-hint-result]'),
            hintExtra: root.querySelector('[data-kronologic-hint-extra]'),
            hintContinue: root.querySelector('[data-kronologic-hint-continue]'),
            hintClose: root.querySelector('[data-kronologic-hint-close]'),
            reveal: root.querySelector('[data-kronologic-reveal]'),
            solutionModal: root.querySelector('[data-kronologic-solution-modal]'),
            solution: root.querySelector('[data-kronologic-solution]'),
            solutionTitle: root.querySelector('[data-kronologic-solution-title]'),
            solutionClose: root.querySelector('[data-kronologic-solution-close]'),
            solutionConfirmView: root.querySelector('[data-kronologic-solution-confirm-view]'),
            solutionResultView: root.querySelector('[data-kronologic-solution-result-view]'),
            solutionRevealBtn: root.querySelector('[data-kronologic-solution-reveal-btn]'),
            solutionCancelBtn: root.querySelector('[data-kronologic-solution-cancel-btn]'),
            quit: root.querySelector('[data-kronologic-quit]'),
            quitModal: root.querySelector('[data-kronologic-quit-modal]'),
            quitConfirm: root.querySelector('[data-kronologic-quit-confirm]'),
            quitCancel: root.querySelector('[data-kronologic-quit-cancel]'),
            quitClose: root.querySelector('[data-kronologic-quit-close]'),
            knowledge: Array.from(root.querySelectorAll('[data-kronologic-knowledge]')),
        };

        this.renderSetup();
        this.enhanceSelects();
        this.renderBoard();
        this.renderKnowledge();
        this.updateHistoryState();
        this.bindEvents();
        this.observeLayout();
        this.restoreSession();
    }

    readConfig() {
        const script = this.root.querySelector(SELECTORS.config);
        if (!script) {
            return null;
        }

        try {
            return JSON.parse(script.textContent || '{}');
        } catch (error) {
            console.error('Kronologic configuration could not be parsed.', error);
            return null;
        }
    }

    renderSetup() {
        const minimum = Number(this.config.game?.players?.minimum) || 2;
        const maximum = Number(this.config.game?.players?.maximum) || 6;
        let previousPlayers = Number(this.elements.playerCount.value);
        if (this.directLink.players && this.directLink.puzzleId === this.config.puzzleId) {
            previousPlayers = this.directLink.players;
        }
        const selectedPlayers = Number.isInteger(previousPlayers)
            && previousPlayers >= minimum
            && previousPlayers <= maximum
            ? previousPlayers
            : minimum;

        this.elements.playerCount.replaceChildren();
        for (let players = minimum; players <= maximum; players += 1) {
            const option = document.createElement('option');
            option.value = String(players);
            option.textContent = `${players} Spieler`;
            option.selected = players === selectedPlayers;
            this.elements.playerCount.append(option);
        }

        this.renderPuzzleOptions(selectedPlayers);
        this.renderDifficulty(selectedPlayers);
        this.renderKnowledge();
        this.syncSelectWidget(this.elements.playerCount);
    }

    renderPuzzleOptions(players) {
        if (!this.elements.puzzleSelect) {
            return;
        }

        const sortedPuzzles = sortPuzzlesByDifficulty(this.puzzles, players);
        this.elements.puzzleSelect.replaceChildren(...sortedPuzzles.map((puzzle) => {
            const option = document.createElement('option');
            option.value = puzzle.puzzleId;
            option.textContent = puzzle.title || puzzle.puzzleId;
            option.selected = puzzle.puzzleId === this.activePuzzle?.puzzleId;
            return option;
        }));
        this.syncSelectWidget(this.elements.puzzleSelect);
    }

    enhanceSelects() {
        [this.elements.puzzleSelect, this.elements.playerCount]
            .filter(Boolean)
            .forEach((select) => this.enhanceSelect(select));
    }

    enhanceSelect(select) {
        if (this.selectWidgets.has(select)) {
            this.syncSelectWidget(select);
            return;
        }

        const control = select.closest('.zgg-kronologic__select-control');
        if (!control) {
            return;
        }

        const button = createElement('button', 'zgg-kronologic__select-button');
        button.type = 'button';
        button.setAttribute('aria-haspopup', 'listbox');
        button.setAttribute('aria-expanded', 'false');

        const value = createElement('span', 'zgg-kronologic__select-value');
        const listbox = createElement('div', 'zgg-kronologic__select-options');
        listbox.hidden = true;
        listbox.setAttribute('role', 'listbox');
        listbox.tabIndex = -1;

        const listboxId = `zgg-kronologic-select-${Math.random().toString(36).slice(2, 10)}`;
        listbox.id = listboxId;
        button.setAttribute('aria-controls', listboxId);
        button.append(value);

        control.classList.add('is-enhanced');
        control.insertBefore(button, select);
        control.append(listbox);

        const widget = { button, control, listbox, select, value, activeIndex: -1 };
        this.selectWidgets.set(select, widget);

        button.addEventListener('click', () => {
            if (listbox.hidden) {
                this.openSelectWidget(widget);
            } else {
                this.closeSelectWidget(widget);
            }
        });
        button.addEventListener('keydown', (event) => this.handleSelectButtonKeydown(event, widget));
        listbox.addEventListener('keydown', (event) => this.handleSelectListKeydown(event, widget));

        this.syncSelectWidget(select);
    }

    syncSelectWidget(select) {
        const widget = this.selectWidgets.get(select);
        if (!widget) {
            return;
        }

        const options = Array.from(select.options);
        const selectedIndex = Math.max(select.selectedIndex, 0);
        widget.activeIndex = selectedIndex;
        widget.value.textContent = options[selectedIndex]?.textContent || '';
        widget.listbox.replaceChildren(...options.map((option, index) => {
            const item = createElement('button', 'zgg-kronologic__select-option', option.textContent);
            item.type = 'button';
            item.setAttribute('role', 'option');
            item.setAttribute('aria-selected', index === selectedIndex ? 'true' : 'false');
            item.dataset.value = option.value;
            item.disabled = option.disabled;
            item.addEventListener('click', () => this.chooseSelectOption(widget, index));
            item.addEventListener('pointermove', () => {
                widget.activeIndex = index;
                this.highlightSelectOption(widget);
            });
            return item;
        }));
        this.highlightSelectOption(widget);
    }

    openSelectWidget(widget, preferredIndex = null) {
        this.selectWidgets.forEach((otherWidget) => {
            if (otherWidget !== widget) {
                this.closeSelectWidget(otherWidget, false);
            }
        });

        widget.activeIndex = preferredIndex ?? Math.max(widget.select.selectedIndex, 0);
        widget.listbox.hidden = false;
        widget.control.classList.add('is-open');
        widget.button.setAttribute('aria-expanded', 'true');
        this.highlightSelectOption(widget);
        window.requestAnimationFrame(() => {
            widget.listbox.querySelector('.is-active')?.focus();
        });
    }

    closeSelectWidget(widget, restoreFocus = true) {
        if (widget.listbox.hidden) {
            return;
        }

        widget.listbox.hidden = true;
        widget.control.classList.remove('is-open');
        widget.button.setAttribute('aria-expanded', 'false');
        if (restoreFocus) {
            widget.button.focus();
        }
    }

    chooseSelectOption(widget, index) {
        const option = widget.select.options[index];
        if (!option || option.disabled) {
            return;
        }

        widget.select.value = option.value;
        widget.select.dispatchEvent(new Event('change', { bubbles: true }));
        this.syncSelectWidget(widget.select);
        this.closeSelectWidget(widget);
    }

    highlightSelectOption(widget) {
        const items = Array.from(widget.listbox.children);
        items.forEach((item, index) => {
            item.classList.toggle('is-active', index === widget.activeIndex);
        });
    }

    moveSelectHighlight(widget, direction) {
        const items = Array.from(widget.select.options);
        if (!items.length) {
            return;
        }

        let nextIndex = widget.activeIndex;
        do {
            nextIndex = (nextIndex + direction + items.length) % items.length;
        } while (items[nextIndex]?.disabled && nextIndex !== widget.activeIndex);

        widget.activeIndex = nextIndex;
        this.highlightSelectOption(widget);
        widget.listbox.children[nextIndex]?.focus();
    }

    handleSelectButtonKeydown(event, widget) {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            const offset = event.key === 'ArrowDown' ? 1 : -1;
            const start = Math.max(widget.select.selectedIndex + offset, 0);
            this.openSelectWidget(widget, Math.min(start, widget.select.options.length - 1));
        }
    }

    handleSelectListKeydown(event, widget) {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            this.moveSelectHighlight(widget, event.key === 'ArrowDown' ? 1 : -1);
            return;
        }

        if (event.key === 'Home' || event.key === 'End') {
            event.preventDefault();
            widget.activeIndex = event.key === 'Home' ? 0 : widget.select.options.length - 1;
            this.highlightSelectOption(widget);
            widget.listbox.children[widget.activeIndex]?.focus();
            return;
        }

        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.chooseSelectOption(widget, widget.activeIndex);
            return;
        }

        if (event.key === 'Escape') {
            event.preventDefault();
            this.closeSelectWidget(widget);
        }
    }

    applyPuzzleConfig(puzzle) {
        if (!puzzle) {
            return;
        }

        this.activePuzzle = puzzle;
        this.config.puzzleId = puzzle.puzzleId;
        this.config.game = puzzle.game || {};
        this.config.difficulty = puzzle.difficulty || {};
        this.config.ratingMetrics = puzzle.ratingMetrics || {};
        this.config.queries = puzzle.queries || [];
        this.config.map = puzzle.map || {};
        this.config.edges = puzzle.edges || [];
        this.config.blockedEdges = puzzle.blockedEdges || [];
        this.config.oneWayEdges = puzzle.oneWayEdges || [];
        this.config.knownRoomTimeLimits = puzzle.knownRoomTimeLimits || {};
        this.config.knownPositions = puzzle.knownPositions || [];
        this.config.knownCurse = puzzle.knownCurse || [];
    }

    switchPuzzle(puzzleId) {
        if (this.session || this.pending) {
            return;
        }

        const puzzle = this.puzzles.find((item) => item.puzzleId === puzzleId);
        if (!puzzle || puzzle === this.activePuzzle) {
            return;
        }

        this.clearStagedPlacement();
        this.clearTokenSelection();
        this.applyPuzzleConfig(puzzle);
        if (this.elements.title) {
            this.elements.title.textContent = puzzle.title || puzzle.puzzleId;
        }
        this.elements.history.replaceChildren();
        this.updateHistoryState();
        this.renderSetup();
        this.renderBoard();
    }

    renderDifficulty(players) {
        const metrics = this.config.ratingMetrics?.[String(players)]
            || selectRatingMetrics(this.config.difficulty, players);
        const ratingConfig = this.config.ratingConfig || {};
        const normalized = normalizePuzzleRatings(this.puzzles, players, ratingConfig)
            .get(this.activePuzzle);
        const difficulty = normalized?.difficulty
            || getDifficultyRating(metrics?.index, ratingConfig);
        const duration = normalized?.duration
            || getDurationRating(metrics?.averageTurns, ratingConfig);

        this.elements.difficulty.replaceChildren(
            this.createRatingCard('difficulty', 'Schwierigkeit', difficulty),
            this.createRatingCard('duration', 'Spieldauer', duration)
        );
    }

    createRatingCard(type, title, rating) {
        const card = createElement('article', `zgg-kronologic__rating-card zgg-kronologic__rating-card--${type}`);
        card.append(createElement('span', 'zgg-kronologic__rating-title', title));

        if (!rating) {
            card.append(createElement('p', 'zgg-kronologic__rating-unavailable', 'Bewertung nicht verfügbar'));
            return card;
        }

        const description = type === 'difficulty'
            ? `Schwierigkeit: ${rating.level} von 5, ${rating.label}`
            : `Spieldauer: ${rating.level} von 5, ${rating.label}`;
        const main = createElement('div', 'zgg-kronologic__rating-main');
        main.append(
            this.createRatingSymbols(type, rating.level, description),
            createElement('strong', 'zgg-kronologic__rating-label', rating.label)
        );
        card.append(main);

        return card;
    }

    createRatingSymbols(type, level, description) {
        const symbols = createElement('span', 'zgg-kronologic__rating-symbols');
        symbols.setAttribute('role', 'img');
        symbols.setAttribute('aria-label', description);

        for (let index = 1; index <= 5; index += 1) {
            symbols.append(this.createRatingIcon(type, index <= level));
        }

        return symbols;
    }

    createRatingIcon(type, active) {
        const namespace = 'http://www.w3.org/2000/svg';
        const svg = document.createElementNS(namespace, 'svg');
        svg.setAttribute('viewBox', '0 0 24 24');
        svg.setAttribute('aria-hidden', 'true');
        svg.setAttribute('focusable', 'false');
        svg.classList.add(
            'zgg-kronologic__rating-icon',
            `zgg-kronologic__rating-icon--${type}`,
            active ? 'is-active' : 'is-inactive'
        );

        if (type === 'difficulty') {
            const star = document.createElementNS(namespace, 'path');
            star.setAttribute(
                'd',
                'M12 2.8 14.85 8.58 21.23 9.51 16.62 14 17.71 20.35 12 17.35 6.29 20.35 7.38 14 2.77 9.51 9.15 8.58Z'
            );
            svg.append(star);
            return svg;
        }

        const bell = document.createElementNS(namespace, 'path');
        bell.setAttribute('d', 'M9 2h6v2H9zM16.95 5.64l1.41-1.41 1.41 1.41-1.41 1.41z');
        const face = document.createElementNS(namespace, 'circle');
        face.setAttribute('cx', '12');
        face.setAttribute('cy', '13');
        face.setAttribute('r', '8');
        const hand = document.createElementNS(namespace, 'path');
        hand.setAttribute('d', 'M12 8v5l3 2');
        svg.append(bell, face, hand);

        return svg;
    }

    renderBoard() {
        this.renderEdgeMarkers();
        this.elements.rooms.replaceChildren();
        this.elements.timeTokens.replaceChildren();
        this.elements.personTokens.replaceChildren();

        this.config.rooms.forEach((room) => {
            const slot = createElement('div', 'zgg-kronologic__room-slot');
            slot.dataset.roomIdentifier = room.identifier;
            slot.style.left = `${room.x}%`;
            slot.style.top = `${room.y}%`;
            slot.style.width = `${room.width}%`;
            slot.style.height = `${room.height}%`;

            const button = createElement('button', 'zgg-kronologic__room');
            button.type = 'button';
            button.setAttribute('aria-label', `Raum ${room.label}`);
            button.append(createElement('span', 'zgg-kronologic__room-label', room.label || room.identifier));
            button.addEventListener('click', () => {
                if (this.selectedToken) {
                    this.stagePlacement(this.selectedToken, room, slot);
                } else if (this.stagedPlacement?.room.identifier === room.identifier) {
                    slot.querySelector('[data-kronologic-confirm-placement]')?.focus();
                } else {
                    this.flashStatus('Wähle zuerst ein Zeit- oder Personenplättchen.', true);
                }
            });

            const placement = createElement('div', 'zgg-kronologic__placement');
            placement.hidden = true;
            const placedToken = createElement('span', 'zgg-kronologic__placed-token');
            placedToken.dataset.kronologicPlacedToken = '';
            const confirm = createElement(
                'button',
                'zgg-kronologic__action-button zgg-kronologic__primary-button zgg-kronologic__action-button--compact zgg-kronologic__placement-confirm',
                'Bestätigen'
            );
            confirm.type = 'button';
            confirm.dataset.kronologicConfirmPlacement = '';
            confirm.addEventListener('click', (event) => {
                event.stopPropagation();
                this.confirmPlacement(room.identifier);
            });
            placement.append(placedToken, confirm);
            slot.append(button, placement);
            this.elements.rooms.append(slot);
        });

        (this.config.game?.times || []).forEach((identifier) => {
            this.elements.timeTokens.append(this.createToken({
                type: 'time',
                identifier: String(identifier),
                label: `Zeitpunkt ${identifier}`,
            }));
        });

        (this.config.game?.persons || []).forEach((identifier) => {
            this.elements.personTokens.append(this.createToken({
                type: 'person',
                identifier,
                label: this.personName(identifier),
            }));
        });
    }

    renderEdgeMarkers() {
        if (!this.elements.edgeMarkers) {
            return;
        }

        const markers = Array.isArray(this.config.mapMarkers)
            ? this.config.mapMarkers
            : (Array.isArray(this.config.markers) ? this.config.markers : []);
        this.elements.edgeMarkers.replaceChildren(...markers.map((marker) => {
            const state = markerStateForEdge(
                marker.edgeId,
                this.config.blockedEdges,
                this.config.oneWayEdges
            );
            const element = createElement(
                'div',
                `kronologic-edge-marker kronologic-edge-marker--${state.state}`
            );
            element.dataset.edgeId = marker.edgeId;
            element.dataset.markerId = marker.id || '';
            element.style.left = `${Number(marker.x) || 0}%`;
            element.style.top = `${Number(marker.y) || 0}%`;
            element.style.setProperty(
                '--zgg-kronologic-marker-rotation',
                `${markerRotation(marker, state)}deg`
            );

            if (state.state === 'normal') {
                element.setAttribute('aria-hidden', 'true');
                return element;
            }

            const startLabel = this.roomName(marker.start);
            const endLabel = this.roomName(marker.end);
            if (state.state === 'blocked') {
                element.setAttribute('role', 'img');
                element.setAttribute('aria-label', `Weg zwischen ${startLabel} und ${endLabel} gesperrt`);
                element.append(this.createBlockedMarkerIcon());
                return element;
            }

            element.setAttribute('role', 'img');
            element.setAttribute(
                'aria-label',
                `Einbahnstraße von ${this.roomName(state.direction.from)} nach ${this.roomName(state.direction.to)}`
            );
            element.append(this.createOneWayMarkerIcon());
            return element;
        }));

        this.renderMapLegend();
    }

    createBlockedMarkerIcon() {
        const namespace = 'http://www.w3.org/2000/svg';
        const svg = document.createElementNS(namespace, 'svg');
        svg.setAttribute('viewBox', '0 0 32 32');
        svg.setAttribute('aria-hidden', 'true');

        if (this.theme === 'cuzco-1450') {
            // Steinmauer-Piktogramm im Stil der Kartenglyphen; füllt das runde Schild aus.
            [
                [5, 6, 10.5, 6], [16.5, 6, 10.5, 6],
                [3, 13, 6.5, 6], [10.5, 13, 11, 6], [22.5, 13, 6.5, 6],
                [5, 20, 10.5, 6], [16.5, 20, 10.5, 6],
            ].forEach(([x, y, width, height]) => {
                const rect = document.createElementNS(namespace, 'rect');
                rect.setAttribute('x', String(x));
                rect.setAttribute('y', String(y));
                rect.setAttribute('width', String(width));
                rect.setAttribute('height', String(height));
                rect.setAttribute('rx', '1');
                svg.append(rect);
            });
            return svg;
        }

        const circle = document.createElementNS(namespace, 'circle');
        circle.setAttribute('cx', '16');
        circle.setAttribute('cy', '16');
        circle.setAttribute('r', '11');
        const line = document.createElementNS(namespace, 'path');
        line.setAttribute('d', 'M8 24 24 8');
        svg.append(circle, line);
        return svg;
    }

    createOneWayMarkerIcon() {
        const namespace = 'http://www.w3.org/2000/svg';
        const svg = document.createElementNS(namespace, 'svg');
        svg.setAttribute('viewBox', '0 0 32 32');
        svg.setAttribute('aria-hidden', 'true');
        // Das runde Schild bleibt aufrecht, nur der Pfeil dreht sich mit dem Weg.
        const glyph = document.createElementNS(namespace, 'g');
        glyph.setAttribute('class', 'zgg-kronologic__marker-glyph');
        svg.append(glyph);

        if (this.theme === 'cuzco-1450') {
            // Gleiche Form wie Paris, nur in der Cuzco-Farbwelt.
            const roadPath = 'M6.5 16h18';
            const headPath = 'M18 9l7 7-7 7';
            const roadOutline = document.createElementNS(namespace, 'path');
            roadOutline.setAttribute('class', 'zgg-kronologic__cuzco-oneway-road-outline');
            roadOutline.setAttribute('d', roadPath);

            const road = document.createElementNS(namespace, 'path');
            road.setAttribute('class', 'zgg-kronologic__cuzco-oneway-road');
            road.setAttribute('d', roadPath);

            const arrowHeadOutline = document.createElementNS(namespace, 'path');
            arrowHeadOutline.setAttribute('class', 'zgg-kronologic__cuzco-oneway-head-outline');
            arrowHeadOutline.setAttribute('d', headPath);

            const arrowHead = document.createElementNS(namespace, 'path');
            arrowHead.setAttribute('class', 'zgg-kronologic__cuzco-oneway-head');
            arrowHead.setAttribute('d', headPath);
            glyph.append(roadOutline, road, arrowHeadOutline, arrowHead);
            return svg;
        }

        const path = document.createElementNS(namespace, 'path');
        path.setAttribute('d', 'M6.5 16h18M18 9l7 7-7 7');
        glyph.append(path);
        return svg;
    }

    renderMapLegend() {
        if (!this.elements.mapLegend) {
            return;
        }

        const hasBlocked = Array.isArray(this.config.blockedEdges) && this.config.blockedEdges.length > 0;
        const hasOneWay = Array.isArray(this.config.oneWayEdges) && this.config.oneWayEdges.length > 0;
        const items = [];
        if (hasBlocked) {
            const item = createElement(
                'span',
                'zgg-kronologic__legend-item zgg-kronologic__legend-item--blocked'
            );
            item.append(this.createBlockedMarkerIcon(), document.createTextNode('Gesperrter Weg'));
            items.push(item);
        }
        if (hasOneWay) {
            const item = createElement(
                'span',
                'zgg-kronologic__legend-item zgg-kronologic__legend-item--one-way'
            );
            item.append(this.createOneWayMarkerIcon(), document.createTextNode('Einbahnstraße'));
            items.push(item);
        }
        this.elements.mapLegend.replaceChildren(...items);
        this.elements.mapLegend.hidden = items.length === 0;
    }

    renderKnowledge() {
        const positions = Array.isArray(this.config.knownPositions)
            ? this.config.knownPositions
            : [];
        const limits = this.config.knownRoomTimeLimits
            && typeof this.config.knownRoomTimeLimits === 'object'
            ? this.config.knownRoomTimeLimits
            : {};
        // Fluch-Variante: wer den Fluch zu einem Zeitpunkt trug (je Zeitpunkt eine Zeile).
        const curseByTime = new Map();
        (Array.isArray(this.config.knownCurse) ? this.config.knownCurse : []).forEach((entry) => {
            curseByTime.set(entry.time, [...(curseByTime.get(entry.time) || []), entry.person]);
        });
            
        const renderData = [
            ...positions.map((position) => ({
                subject: this.personName(position.person),
                room: `Zeitpunkt ${position.time}`,
                result: this.roomName(position.room)
            })),
            ...Object.entries(limits).map(([code, limit]) => {
                const parsed = this.parseRoomTimeCode(code);
                const personWord = Number(limit) === 1 ? 'Person' : 'Personen';
                return parsed ? {
                    subject: `Zeitpunkt ${parsed.time}`,
                    room: this.roomName(parsed.room),
                    result: `max. ${limit} ${personWord}`
                } : {
                    subject: code,
                    room: '',
                    result: `max. ${limit} ${personWord}`
                };
            }),
            ...Array.from(curseByTime, ([time, persons]) => ({
                subject: 'Fluch',
                room: `Zeitpunkt ${time}`,
                result: persons.map((person) => this.personName(person)).join(', ')
            })),
        ];

        this.elements.knowledge.forEach((section) => {
            const list = section.querySelector('ul');
            if (list) {
                const elements = renderData.map((data) => {
                    const item = createElement('li', 'zgg-kronologic__history-item');
                    const heading = createElement('div', 'zgg-kronologic__history-query');
                    
                    if (data.room) {
                        const route = createElement('div', 'zgg-kronologic__history-route');
                        const arrow = createElement('span', 'zgg-kronologic__history-arrow', '–');
                        arrow.setAttribute('aria-hidden', 'true');
                        route.append(
                            createElement('span', 'zgg-kronologic__history-subject', data.subject),
                            arrow,
                            createElement('span', 'zgg-kronologic__history-room', data.room)
                        );
                        heading.append(route);
                    } else {
                        heading.append(createElement('span', 'zgg-kronologic__history-subject', data.subject));
                    }
                    
                    const result = createElement('span', 'zgg-kronologic__history-count', data.result);
                    item.append(heading, result);
                    return item;
                });
                list.replaceChildren(...elements);
            }
            section.hidden = renderData.length === 0;
        });
    }

    parseRoomTimeCode(code) {
        const rooms = Array.isArray(this.config.game?.rooms) ? this.config.game.rooms : [];
        const times = Array.isArray(this.config.game?.times) ? this.config.game.times : [];
        for (const room of rooms) {
            for (const time of times) {
                if (code === `${room}${time}`) {
                    return { room, time };
                }
            }
        }
        return null;
    }

    roomName(identifier) {
        return this.config.rooms.find((room) => room.identifier === identifier)?.label || identifier;
    }

    createToken(token) {
        const button = createElement(
            'button',
            `zgg-kronologic__token zgg-kronologic__token--${token.type}`
        );
        button.type = 'button';
        button.dataset.tokenType = token.type;
        button.dataset.tokenIdentifier = token.identifier;
        button.append(createElement('span', 'zgg-kronologic__token-label', token.label));
        button.addEventListener('click', () => {
            if (button.dataset.suppressClick === 'true') {
                return;
            }
            this.selectToken(token, button);
        });
        button.addEventListener('pointerdown', (event) => this.beginTokenDrag(event, token, button));

        return button;
    }

    selectToken(token, button) {
        const alreadySelected = this.selectedToken?.type === token.type
            && this.selectedToken?.identifier === token.identifier;

        this.root.querySelectorAll('.zgg-kronologic__token.is-selected')
            .forEach((element) => element.classList.remove('is-selected'));

        if (alreadySelected) {
            this.selectedToken = null;
            return;
        }

        this.selectedToken = token;
        button.classList.add('is-selected');
        this.flashStatus(`${token.label} gewählt – jetzt einen Raum antippen.`);
    }

    beginTokenDrag(event, token, button) {
        if (this.pending || (event.button !== undefined && event.button !== 0)) {
            return;
        }

        const pointerId = event.pointerId;
        const startX = event.clientX;
        const startY = event.clientY;
        let dragging = false;
        let ghost = null;

        const move = (moveEvent) => {
            if (moveEvent.pointerId !== pointerId) {
                return;
            }

            const distance = Math.hypot(moveEvent.clientX - startX, moveEvent.clientY - startY);
            if (!dragging && distance < 7) {
                return;
            }

            if (!dragging) {
                dragging = true;
                ghost = button.cloneNode(true);
                ghost.classList.add('zgg-kronologic__drag-ghost');
                ghost.removeAttribute('data-token-type');
                this.root.append(ghost);
                button.classList.add('is-dragging');
                this.root.classList.add('is-dragging-token');
            }

            moveEvent.preventDefault();
            ghost.style.left = `${moveEvent.clientX}px`;
            ghost.style.top = `${moveEvent.clientY}px`;
            this.updateDropTarget(moveEvent.clientX, moveEvent.clientY);
        };

        const finish = (upEvent) => {
            if (upEvent.pointerId !== pointerId) {
                return;
            }

            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', finish);
            window.removeEventListener('pointercancel', finish);

            if (dragging) {
                upEvent.preventDefault();
                button.dataset.suppressClick = 'true';
                window.setTimeout(() => {
                    delete button.dataset.suppressClick;
                }, 0);

                const roomElement = document.elementFromPoint(upEvent.clientX, upEvent.clientY)
                    ?.closest('[data-room-identifier]');
                const room = roomElement
                    ? this.config.rooms.find((item) => item.identifier === roomElement.dataset.roomIdentifier)
                    : null;

                if (room) {
                    this.stagePlacement(token, room, roomElement);
                }
            }

            ghost?.remove();
            button.classList.remove('is-dragging');
            this.root.classList.remove('is-dragging-token');
            this.clearDropTarget();
        };

        window.addEventListener('pointermove', move, { passive: false });
        window.addEventListener('pointerup', finish);
        window.addEventListener('pointercancel', finish);
    }

    updateDropTarget(x, y) {
        const next = document.elementFromPoint(x, y)?.closest('[data-room-identifier]') || null;
        if (next === this.dropTarget) {
            return;
        }

        this.clearDropTarget();
        this.dropTarget = next;
        this.dropTarget?.classList.add('is-drop-target');
    }

    clearDropTarget() {
        this.dropTarget?.classList.remove('is-drop-target');
        this.dropTarget = null;
    }

    stagePlacement(token, room, slot = null) {
        if (!this.session || this.pending || !this.elements.veil.hidden || !this.elements.hintModal.hidden) {
            return;
        }

        const roomSlot = slot || Array.from(this.elements.rooms.querySelectorAll('[data-room-identifier]'))
            .find((element) => element.dataset.roomIdentifier === room.identifier);
        if (!roomSlot) {
            return;
        }

        this.clearStagedPlacement();
        this.clearTokenSelection();
        this.stagedPlacement = { token, room, slot: roomSlot };

        const placement = roomSlot.querySelector('.zgg-kronologic__placement');
        const placedToken = placement?.querySelector('[data-kronologic-placed-token]');
        if (placedToken) {
            placedToken.textContent = token.label;
        }
        if (placement) {
            placement.hidden = false;
        }

        roomSlot.classList.add('has-staged-token');

        const query = `${token.identifier}${room.identifier}`;
        const isDuplicate = !this.repeatQueriesAllowed && this.queriesUsed.has(query);

        if (isDuplicate) {
            this.flashStatus(`Abfrage ${token.label} bei ${room.label} wurde bereits verwendet.`, true);
        } else {
            this.flashStatus(`${token.label} liegt auf ${room.label}. Bestätigen oder durch ein anderes Plättchen ersetzen.`);
        }

        const confirm = roomSlot.querySelector('[data-kronologic-confirm-placement]');
        if (confirm) {
            confirm.disabled = isDuplicate;
            if (!isDuplicate) {
                window.requestAnimationFrame(() => {
                    confirm.focus();
                });
            }
        }
    }

    clearStagedPlacement() {
        if (!this.stagedPlacement) {
            return;
        }

        const { slot } = this.stagedPlacement;
        slot.classList.remove('has-staged-token');
        const placement = slot.querySelector('.zgg-kronologic__placement');
        if (placement) {
            placement.hidden = true;
            const confirm = placement.querySelector('[data-kronologic-confirm-placement]');
            if (confirm) {
                confirm.disabled = false;
            }
        }
        this.stagedPlacement = null;
    }

    async confirmPlacement(roomIdentifier) {
        if (
            !this.stagedPlacement
            || this.stagedPlacement.room.identifier !== roomIdentifier
            || this.pending
        ) {
            return;
        }

        const { token, room, slot } = this.stagedPlacement;
        const confirm = slot.querySelector('[data-kronologic-confirm-placement]');
        if (confirm) {
            confirm.disabled = true;
        }

        const completed = await this.playQuery(token, room);
        if (confirm) {
            confirm.disabled = false;
        }
    }

    bindEvents() {
        this.elements.puzzleSelect?.addEventListener('change', () => {
            this.switchPuzzle(this.elements.puzzleSelect.value);
        });
        this.elements.playerCount.addEventListener('change', () => {
            const players = Number(this.elements.playerCount.value);
            this.renderPuzzleOptions(players);
            this.renderDifficulty(players);
        });
        this.elements.start.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.startGame();
        });
        this.elements.ready.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.dismissVeil();
        });
        this.elements.hintContinue.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.finishHint();
        });
        this.elements.hintClose.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.finishHint();
        });
        this.elements.reveal.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.openSolutionConfirm();
        });
        this.elements.solutionRevealBtn.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.revealSolution();
        });
        this.elements.solutionCancelBtn.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.elements.solutionModal.hidden = true;
        });
        this.elements.solutionClose.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.elements.solutionModal.hidden = true;
        });
        this.elements.quit.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.openQuitConfirm();
        });
        this.elements.quitConfirm.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.endGame();
        });
        [this.elements.quitCancel, this.elements.quitClose].forEach((button) => {
            button.addEventListener('click', (event) => {
                event.preventDefault();
                event.stopPropagation();
                this.elements.quitModal.hidden = true;
            });
        });
        document.addEventListener('pointerdown', (event) => {
            this.selectWidgets.forEach((widget) => {
                if (!widget.control.contains(event.target)) {
                    this.closeSelectWidget(widget, false);
                }
            });
        });
    }

    observeLayout() {
        const update = () => {
            const rect = this.root.getBoundingClientRect();
            const useSide = rect.width >= 980 && rect.width / Math.max(window.innerHeight, 1) >= 1.18;
            this.root.classList.toggle('zgg-kronologic--side-dock', useSide);
        };

        if ('ResizeObserver' in window) {
            this.resizeObserver = new ResizeObserver(update);
            this.resizeObserver.observe(this.root);
        } else {
            window.addEventListener('resize', update);
        }
        update();
    }

    async startGame() {
        if (this.pending) {
            return;
        }

        this.setPending(true);
        try {
            this.queriesUsed.clear();
            this.repeatQueriesAllowed = this.elements.repeatQueries ? this.elements.repeatQueries.checked : true;

            const response = await this.request('/sessions', {
                puzzle_id: this.config.puzzleId,
                players: Number(this.elements.playerCount.value),
                repeat_queries: this.repeatQueriesAllowed,
                solution_locked: this.directLink.solutionLocked,
            });
            this.setSolutionLocked(this.directLink.solutionLocked);
            this.session = response;
            this.currentPlayer = response.current_player;
            writeSessionCookie({
                token: response.token,
                puzzleId: this.config.puzzleId,
            });
            this.elements.setup.hidden = true;
            this.elements.game.hidden = false;
            this.elements.status.hidden = false;
            this.showVeil(this.currentPlayer, true);
        } catch (error) {
            this.flashStatus(error.message, true);
        } finally {
            this.setPending(false);
        }
    }

    async restoreSession() {
        const saved = readSessionCookie();
        const linked = this.directLink.puzzleId === this.config.puzzleId ? this.directLink : null;
        // Ein Direktlink auf ein anderes Puzzle hat Vorrang vor dem gespeicherten Spielstand.
        const puzzle = saved && typeof saved.token === 'string' && saved.token
            && (!linked || saved.puzzleId === linked.puzzleId)
            ? this.puzzles.find((item) => item.puzzleId === saved.puzzleId)
            : null;
        if (!puzzle) {
            if (linked?.autoStart) {
                this.startGame();
            }
            return;
        }

        let expired = false;
        this.setPending(true);
        try {
            const state = await this.request(`/sessions/${saved.token}`, null, 'GET');
            this.resumeSession(saved.token, puzzle, state);
        } catch (error) {
            if (error.status === 404) {
                clearSessionCookie();
                expired = true;
            } else {
                this.flashStatus('Der gespeicherte Spielstand konnte nicht geladen werden.', true);
            }
        } finally {
            this.setPending(false);
        }
        if (expired && linked?.autoStart) {
            this.startGame();
        }
    }

    resumeSession(token, puzzle, state) {
        this.clearStagedPlacement();
        this.clearTokenSelection();
        this.applyPuzzleConfig(puzzle);
        if (this.elements.title) {
            this.elements.title.textContent = puzzle.title || puzzle.puzzleId;
        }

        this.renderSetup();
        this.elements.playerCount.value = String(state.players);
        this.syncSelectWidget(this.elements.playerCount);
        this.renderPuzzleOptions(state.players);
        this.renderDifficulty(state.players);
        this.renderBoard();
        this.renderKnowledge();

        this.session = { token };
        this.currentPlayer = state.current_player;
        this.repeatQueriesAllowed = !!state.repeat_queries;
        this.setSolutionLocked(!!state.solution_locked);
        if (this.elements.repeatQueries) {
            this.elements.repeatQueries.checked = this.repeatQueriesAllowed;
        }

        this.queriesUsed.clear();
        this.elements.history.replaceChildren();
        (Array.isArray(state.history) ? state.history : []).forEach((turn) => {
            this.queriesUsed.add(turn.query);
            const parsed = this.parseQuery(turn.query);
            if (parsed) {
                this.appendHistory({ player: turn.player, count: turn.count }, parsed.token, parsed.room);
            }
        });
        this.updateHistoryState();

        writeSessionCookie({ token, puzzleId: puzzle.puzzleId });
        this.elements.setup.hidden = true;
        this.elements.game.hidden = false;
        this.elements.status.hidden = false;
        this.showVeil(this.currentPlayer, state.turn_number === 1);
    }

    parseQuery(query) {
        const rooms = Array.isArray(this.config.rooms) ? this.config.rooms : [];
        const persons = Array.isArray(this.config.game?.persons) ? this.config.game.persons : [];
        const times = Array.isArray(this.config.game?.times) ? this.config.game.times : [];

        for (const person of persons) {
            const room = rooms.find((item) => `${person}${item.identifier}` === query);
            if (room) {
                return {
                    token: { type: 'person', identifier: person, label: this.personName(person) },
                    room,
                };
            }
        }

        for (const time of times) {
            const identifier = String(time);
            const room = rooms.find((item) => `${identifier}${item.identifier}` === query);
            if (room) {
                return {
                    token: { type: 'time', identifier, label: `Zeitpunkt ${identifier}` },
                    room,
                };
            }
        }

        return null;
    }

    openQuitConfirm() {
        if (!this.session || this.pending) {
            return;
        }

        this.elements.quitModal.hidden = false;
        window.requestAnimationFrame(() => this.elements.quitCancel.focus());
    }

    async endGame() {
        if (this.pending) {
            return;
        }

        this.elements.quitModal.hidden = true;
        const token = this.session?.token;
        clearSessionCookie();

        if (token) {
            this.setPending(true);
            try {
                await this.request(`/sessions/${token}`, null, 'DELETE');
            } catch (error) {
                // Serverseitig läuft die Session ohnehin ab – lokal trotzdem zurücksetzen.
            } finally {
                this.setPending(false);
            }
        }

        this.resetToSetup();
    }

    resetToSetup() {
        this.session = null;
        this.currentPlayer = null;
        this.lastTurn = null;
        this.queriesUsed.clear();
        this.clearStagedPlacement();
        this.clearTokenSelection();
        this.elements.history.replaceChildren();
        this.updateHistoryState();
        this.elements.hintModal.hidden = true;
        this.elements.solutionModal.hidden = true;
        this.elements.veil.hidden = true;
        this.root.classList.remove('is-veiled');
        this.elements.game.hidden = true;
        this.elements.status.hidden = true;
        this.elements.status.textContent = '';
        this.elements.status.classList.remove('is-error');
        this.elements.setup.hidden = false;
        window.requestAnimationFrame(() => this.elements.start.focus());
    }

    showVeil(player, firstTurn = false) {
        this.elements.hintModal.hidden = true;
        this.elements.veilTitle.textContent = firstTurn
            ? `Spieler ${player} beginnt`
            : `Spieler ${player} ist am Zug`;
        this.elements.veil.hidden = false;
        this.root.classList.add('is-veiled');
        window.requestAnimationFrame(() => this.elements.ready.focus());
    }

    dismissVeil() {
        this.elements.veil.hidden = true;
        this.root.classList.remove('is-veiled');
        this.updateTurnStatus();
        window.requestAnimationFrame(() => {
            this.root.querySelector('.zgg-kronologic__token')?.focus();
        });
    }

    async playQuery(token, room) {
        if (!this.session || this.pending || !this.elements.veil.hidden || !this.elements.hintModal.hidden) {
            return false;
        }

        const query = `${token.identifier}${room.identifier}`;
        if (!this.config.queries.includes(query)) {
            this.flashStatus(`Die Abfrage ${query} existiert nicht in der Puzzle-Datei.`, true);
            return false;
        }

        this.setPending(true);
        this.root.querySelectorAll('.zgg-kronologic__room-slot')
            .forEach((element) => element.classList.remove('is-drop-target'));

        try {
            const response = await this.request(`/sessions/${this.session.token}/turn`, {
                query,
                player: this.currentPlayer,
            });
            this.clearStagedPlacement();
            this.lastTurn = { response, token, room };
            this.appendHistory(response, token, room);
            this.showPrivateHint(response, token, room);
            this.queriesUsed.add(query);
            return true;
        } catch (error) {
            this.flashStatus(error.message, true);
            return false;
        } finally {
            this.setPending(false);
        }
    }

    showPrivateHint(response, token, room) {
        const queryType = token.type === 'time' ? 'Wann' : 'Wer';
        const queryValue = token.type === 'time' ? token.identifier : token.label;
        const queryLabel = `${queryType}: ${queryValue} · Wo: ${room.label}`;

        this.elements.hintQuery.replaceChildren(
            this.createQueryPart(queryType, queryValue),
            createElement('span', 'zgg-kronologic__hint-arrow', '×'),
            this.createQueryPart('Wo', room.label)
        );
        this.elements.hintQuery.setAttribute('aria-label', queryLabel);
        this.elements.hintResult.replaceChildren(
            this.createPublicHintCard(response, token, room),
            this.createPrivateHintCard(response, token, room)
        );

        this.elements.hintExtra.hidden = !response.extra_turn;
        this.elements.hintExtra.textContent = response.extra_turn
            ? 'Anzahl 0: Du bist direkt noch einmal am Zug.'
            : '';
        this.elements.hintContinue.textContent = response.extra_turn
            ? 'Schließen und direkt nochmal ziehen'
            : 'Hinweis merken und Gerät abdecken';
        this.elements.hintModal.hidden = false;
        window.requestAnimationFrame(() => this.elements.hintContinue.focus());
    }

    createQueryPart(label, value) {
        const part = createElement('span', 'zgg-kronologic__hint-query-part');
        part.append(
            createElement('small', '', label),
            createElement('strong', '', value)
        );
        return part;
    }

    createPublicHintCard(response, token, room) {
        const card = createElement('section', 'zgg-kronologic__hint-panel zgg-kronologic__hint-panel--public');
        const sentence = token.type === 'person'
            ? `Mal: ${token.label} bei ${room.label}`
            : `${response.count === 1 ? 'Person' : 'Personen'}: Zeit ${token.identifier}, ${room.label}`;

        card.append(
            createElement('span', 'zgg-kronologic__hint-panel-label', 'Öffentlich'),
            createElement('strong', 'zgg-kronologic__hint-value', String(response.count)),
            createElement('p', 'zgg-kronologic__hint-sentence', sentence)
        );
        return card;
    }

    createPrivateHintCard(response, token, room) {
        const identifier = response.private_detail;
        const card = createElement('section', 'zgg-kronologic__hint-panel zgg-kronologic__hint-panel--private');
        card.append(createElement('span', 'zgg-kronologic__hint-panel-label', 'Privat'));

        if (!identifier || identifier === '-') {
            card.append(
                createElement('strong', 'zgg-kronologic__hint-value zgg-kronologic__hint-value--empty', '—'),
                createElement('p', 'zgg-kronologic__hint-sentence', 'Kein Zusatz-Hinweis')
            );
            return card;
        }

        if (token.type === 'person') {
            card.append(
                createElement('strong', 'zgg-kronologic__hint-value', String(identifier)),
                createElement(
                    'p',
                    'zgg-kronologic__hint-sentence',
                    `Zeitpunkt: ${token.label} bei ${room.label}`
                )
            );
            return card;
        }

        const person = this.personShortName(identifier);
        card.append(
            createElement('strong', 'zgg-kronologic__hint-value zgg-kronologic__hint-value--name', person),
            createElement(
                'p',
                'zgg-kronologic__hint-sentence',
                `Zeit ${token.identifier}, ${room.label}`
            )
        );
        return card;
    }

    finishHint() {
        if (!this.lastTurn) {
            this.elements.hintModal.hidden = true;
            return;
        }

        const { response } = this.lastTurn;
        this.elements.hintModal.hidden = true;
        this.clearTokenSelection();
        this.currentPlayer = response.next_player;
        this.lastTurn = null;

        if (response.extra_turn) {
            this.updateTurnStatus('Du bist wegen des Nuller-Hinweises noch einmal dran.');
        } else {
            this.showVeil(this.currentPlayer);
        }
    }

    appendHistory(response, token, room) {
        const item = createElement('li', 'zgg-kronologic__history-item');
        const heading = createElement('div', 'zgg-kronologic__history-query');
        const route = createElement('div', 'zgg-kronologic__history-route');
        route.setAttribute('aria-label', `${token.label} bei ${room.label}`);
        const arrow = createElement('span', 'zgg-kronologic__history-arrow', '–');
        arrow.setAttribute('aria-hidden', 'true');
        route.append(
            createElement('span', 'zgg-kronologic__history-subject', token.label),
            arrow,
            createElement('span', 'zgg-kronologic__history-room', room.label)
        );
        heading.append(
            createElement('strong', 'zgg-kronologic__history-player', `Spieler ${response.player}`),
            route
        );
        const result = createElement(
            'span',
            'zgg-kronologic__history-count',
            token.type === 'person'
                ? `${response.count} Mal`
                : `${response.count} ${response.count === 1 ? 'Person' : 'Personen'}`
        );
        item.append(heading, result);
        this.elements.history.prepend(item);
        this.updateHistoryState();
    }

    updateHistoryState() {
        const isEmpty = !this.elements.history?.children.length;
        this.elements.history?.closest('.zgg-kronologic__history')?.classList.toggle('is-empty', isEmpty);
        if (this.elements.historyEmpty) {
            this.elements.historyEmpty.hidden = !isEmpty;
        }
    }

    clearTokenSelection() {
        this.selectedToken = null;
        this.root.querySelectorAll('.zgg-kronologic__token.is-selected')
            .forEach((element) => element.classList.remove('is-selected'));
    }

    updateTurnStatus(message = '') {
        this.elements.status.classList.remove('is-error');
        this.elements.status.textContent = message
            ? `Spieler ${this.currentPlayer} · ${message}`
            : `Spieler ${this.currentPlayer} ist am Zug`;
    }

    flashStatus(message, isError = false) {
        if (!this.elements?.status) {
            return;
        }

        this.elements.status.hidden = false;
        this.elements.status.textContent = message;
        this.elements.status.classList.toggle('is-error', isError);
    }

    setPending(pending) {
        this.pending = pending;
        this.root.classList.toggle('is-pending', pending);
        if (this.elements?.start) {
            this.elements.start.disabled = pending;
        }
    }

    personName(identifier) {
        return this.config.aliases?.[identifier] || identifier;
    }

    personShortName(identifier) {
        return this.config.shortNames?.[identifier] || this.personName(identifier);
    }

    openSolutionConfirm() {
        if (!this.session || this.pending || this.solutionLocked) {
            return;
        }
        this.elements.solutionConfirmView.hidden = false;
        this.elements.solutionResultView.hidden = true;
        this.elements.solutionModal.hidden = false;
        window.requestAnimationFrame(() => this.elements.solutionRevealBtn.focus());
    }

    async revealSolution() {
        if (!this.session || this.pending) {
            return;
        }

        this.setPending(true);
        try {
            const response = await this.request(`/sessions/${this.session.token}/reveal-solution`, {
                confirm: true,
            });
            const solution = response.solution || {};
            const holders = response.curse?.holders;
            const isCurse = holders && typeof holders === 'object';
            if (this.elements.solutionTitle) {
                this.elements.solutionTitle.textContent = isCurse
                    ? 'Wer den Fluch trug'
                    : this.elements.solutionTitle.dataset.defaultTitle;
            }
            if (isCurse) {
                // Fluch-Variante: je Zeitpunkt, wer betroffen war.
                this.elements.solution.replaceChildren(...Object.entries(holders).map(([time, persons]) => (
                    this.solutionPart(
                        `Zeitpunkt ${time}`,
                        persons.map((person) => this.personName(person)).join(', ') || '–'
                    )
                )));
            } else {
                const room = this.config.rooms.find((item) => item.identifier === solution.room);
                this.elements.solution.replaceChildren(
                    this.solutionPart('Zeitpunkt', String(solution.time ?? '–')),
                    this.solutionPart('Raum', room?.label || solution.room || '–'),
                    this.solutionPart('Person', this.personName(solution.partner || '–'))
                );
            }
            this.elements.solutionConfirmView.hidden = true;
            this.elements.solutionResultView.hidden = false;
            window.requestAnimationFrame(() => this.elements.solutionClose.focus());
        } catch (error) {
            this.flashStatus(error.message, true);
            this.elements.solutionModal.hidden = true;
        } finally {
            this.setPending(false);
        }
    }

    setSolutionLocked(locked) {
        this.solutionLocked = locked;
        if (this.elements.reveal) {
            this.elements.reveal.hidden = locked;
        }
    }

    solutionPart(label, value) {
        const part = createElement('div', 'zgg-kronologic__solution-part');
        part.append(
            createElement('span', '', label),
            createElement('strong', '', value)
        );
        return part;
    }

    async request(path, data, method = 'POST') {
        const options = {
            method,
            credentials: 'same-origin',
            headers: {
                Accept: 'application/json',
            },
            cache: 'no-store',
        };
        if (method !== 'GET' && data !== null && data !== undefined) {
            options.headers['Content-Type'] = 'application/json';
            options.body = JSON.stringify(data);
        }

        const response = await fetch(`${this.config.restUrl}${path}`, options);
        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
            const error = new Error(payload.message || 'Die Spielaktion konnte nicht ausgeführt werden.');
            error.status = response.status;
            throw error;
        }

        return payload;
    }
}

const initKronologicGames = () => {
    document.querySelectorAll(SELECTORS.root).forEach((root) => {
        if (root.dataset.kronologicReady === 'true') {
            return;
        }
        root.dataset.kronologicReady = 'true';
        new KronologicGame(root);
    });
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initKronologicGames);
} else {
    initKronologicGames();
}

export default KronologicGame;
